import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { startCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Icon from 'shared/components/Icon';
import { useGetTransactionDetailsBulk } from 'shared/hooks';

type TransactionPropType = {
  surveyId: string;
  transactionId: string;
};

function TransactionProperties(props: TransactionPropType) {
  const { t } = useTranslation();
  const { surveyId, transactionId } = props;
  const [isOpen, setIsOpen] = React.useState(true);
  const { data: triggers } = useGetTransactionDetailsBulk({
    surveyId: surveyId ? surveyId : '',
    transactionIds: [transactionId],
    enabled: Boolean(transactionId?.length),
  });

  const transaction_meta = React.useMemo(() => {
    if (triggers?.length > 0) {
      const resultArr: any[] = [];
      for (const property in triggers[0].transaction_meta) {
        resultArr.push({
          name: property,
          value: triggers[0].transaction_meta[property],
        });
      }
      return resultArr;
    }
    return [];
  }, [triggers]);

  const transactionPropCount = transaction_meta.length;

  if (transactionPropCount)
    return (
      <StyledTransactionBox>
        <div
          className={`header ${isOpen ? 'expand' : 'close'}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {`${t(
            'responses.transaction_properties.title',
          )} (${transactionPropCount})`}
          <Icon glyph="arrow-down" width={12} height={12} />
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.section
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            >
              <div className="property-list">
                {transaction_meta.map((item: any) => (
                  <div className="property" key={item.name}>
                    <div
                      className="property-label"
                      title={startCase(item.name)}
                    >
                      {startCase(item.name)}
                    </div>
                    <div className="property-value" title={item.value}>
                      {String(item.value)}
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </StyledTransactionBox>
    );
  else return <></>;
}

export default TransactionProperties;

const StyledTransactionBox = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  margin-bottom: 1rem;

  .header {
    padding: 8px 16px;
    font-weight: 400;
    font-size: 14px;
    line-height: 30px;
    color: var(--base-color);
    display: flex;
    align-items: center;
    cursor: pointer;
    justify-content: space-between;

    &.expand svg {
      transform: rotate(180deg);
      transition: all 0.5s ease-in-out;
    }
  }

  .property-list {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    padding: 8px 16px;
    margin-bottom: 1rem;

    .property-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      opacity: 0.6;
      color: #000;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .property-value {
      font-weight: 700;
      font-size: 14px;
      line-height: 22px;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      color: #000;
      opacity: 0.6;
    }
  }
`;
